import axios from "axios";
import { toast } from "sonner";
import { clearSession, saveSession, sessionRevision } from "@/lib/authSession";

const BASE = process.env.REACT_APP_API_GATEWAY === "true" ? "" : process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api/sb`;

const api = axios.create({ baseURL: API, timeout: 30000 });

api.interceptors.request.use((config) => {
  config._authRevision = sessionRevision();
  const token = sessionStorage.getItem("cdoc_token");
  if (token && !config.url?.startsWith("/auth/login")) config.headers.Authorization = `Bearer ${token}`;
  const acting = sessionStorage.getItem("cdoc_acting_tenant");
  if (acting) config.headers["X-Acting-Tenant"] = acting;
  return config;
});

let refreshing = null;
export function refreshSession() {
  if (refreshing && refreshing.revision === sessionRevision()) return refreshing.promise;
  const revision = sessionRevision();
  const refresh_token = sessionStorage.getItem("cdoc_refresh");
  if (!refresh_token) return Promise.reject(Object.assign(new Error("Sessione scaduta"), { response: { status: 401 } }));
  const entry = { revision };
  entry.promise = axios.post(`${API}/auth/refresh`, { refresh_token }, {
    timeout: 20000, headers: sessionStorage.getItem("cdoc_acting_tenant") ? { "X-Acting-Tenant": sessionStorage.getItem("cdoc_acting_tenant") } : {},
  })
    .then(({ data }) => { saveSession(data, revision); return data.token; })
    .finally(() => { if (refreshing === entry) refreshing = null; });
  refreshing = entry;
  return entry.promise;
}

api.interceptors.response.use(
  (response) => {
    if (response.config._authRevision !== sessionRevision()) return Promise.reject(new Error("Sessione cambiata"));
    if (["post","put","delete","patch"].includes(response.config.method) && /\/(documents|upload|tech-docs)/.test(response.config.url || "")) {
      window.dispatchEvent(new Event("cantieredoc:documents-changed"));
    }
    return response;
  },
  async (error) => {
    const original = error.config;
    if (!original || original._authRevision !== sessionRevision()) return Promise.reject(error);
    const authEndpoint = ["/auth/login", "/auth/refresh", "/auth/logout"].some((path) => original.url?.startsWith(path));
    if (error.response?.status === 401 && !original._retry && !authEndpoint) {
      original._retry = true;
      const revision = sessionRevision();
      try {
        const token = await refreshSession();
        if (revision !== sessionRevision()) throw new Error("Sessione cambiata");
        original.headers.Authorization = `Bearer ${token}`;
        return api(original);
      } catch (refreshError) {
        if (revision === sessionRevision() && [400, 401, 403].includes(refreshError.response?.status)) clearSession();
        return Promise.reject(refreshError);
      }
    }
    if (error.response?.status === 401 && !authEndpoint) clearSession();
    return Promise.reject(error);
  }
);

export async function fetchFileUrl(docId, signal) {
  const { data } = await api.get(`/documents/${encodeURIComponent(docId)}/file`, {
    responseType: "blob", timeout: 120000, signal,
  });
  return URL.createObjectURL(data);
}

export function zipUrl(companyId) {
  return `/export/company/${encodeURIComponent(companyId)}/zip`;
}

export function reportUrl(kind, id) {
  return `/report/${encodeURIComponent(kind)}/${encodeURIComponent(id)}/pdf`;
}

export function exportUrl(path) { return path; }

export async function downloadFile(path) {
  try {
    if (!path.startsWith("/") || path.startsWith("//")) throw new Error("Invalid download path");
    const { data, headers } = await api.get(path, { responseType: "blob", timeout: 120000 });
    const url = URL.createObjectURL(data);
    const disposition = headers["content-disposition"] || "";
    const match = disposition.match(/filename="?([^";]+)"?/i);
    const type = headers["content-type"] || "";
    const ext = type.includes("pdf") ? "pdf" : type.includes("zip") ? "zip" : "csv";
    const link = document.createElement("a");
    link.href = url;
    link.download = match ? match[1].replace(/[\\/]/g, "_") : `cantieredoc.${ext}`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (error) {
    if (error.response?.data instanceof Blob) {
      try { error.response.data = JSON.parse(await error.response.data.text()); } catch (_) { /* non-JSON error */ }
    }
    toast.error(errMsg(error, "Download non riuscito"));
  }
}

export function errMsg(e, fallback = "Si è verificato un errore") {
  if (e?.code === "ECONNABORTED") return "La richiesta ha impiegato troppo tempo. Riprova.";
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(" ");
  if (!e?.response) return "Connessione non riuscita. Controlla la rete e riprova.";
  return fallback;
}

export default api;
